import React from "react";
import { HeaderSection } from "../headers/HeaderSection";

type Step = {
  number: number;
  title: string;
  description: string;
};

type ProcedureStepsProps = {
  title?: string;
  subtitle?: string;
  steps: Step[];
  backgrounds?: string[];
};

const default_Backgrounds = [
  "bg-sky-300",
  "bg-sky-400",
  "bg-emerald-300",
  "bg-emerald-400",
];

const ProcedureSteps: React.FC<ProcedureStepsProps> = ({
  title = "",
  subtitle = "",
  steps,
  backgrounds = default_Backgrounds,
}) => {
  return (
    <section className="px-6 -mt-5">
      <HeaderSection title={title} subtitle={subtitle} />

      <div className="overflow-hidden">
        <div className="flex justify-center">
          {/* Desktop Version (unchanged) */}
          <div className="hidden md:flex justify-center w-full mt-5 md:mt-0 max-w-7xl">
            {steps.map((step, index) => (
              <div
                key={`desktop-${step.number}`}
                className={`
                  ${backgrounds[index % backgrounds.length]}
                  flex-shrink-0
                  w-full
                  md:basis-1/3
                  lg:basis-1/4
                  relative
                  ${index === 0 ? "rounded-l-2xl" : ""}
                  ${index === steps.length - 1 ? "rounded-r-2xl" : ""}
                `}
                style={{
                  clipPath:
                    index === 0
                      ? "polygon(0 0, calc(100% - 25px) 0, 100% 60%, calc(100% - 25px) 100%, 0 100%)"
                      : index === steps.length - 1
                      ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 25px 60%)"
                      : "polygon(0 0, calc(100% - 25px) 0, 100% 60%, calc(100% - 25px) 100%, 0 100%, 25px 60%)",
                  marginLeft: index !== 0 ? "-25px" : "0",
                  zIndex: steps.length - index,
                  borderTopLeftRadius: index === 0 ? "16px" : "0px",
                  borderBottomLeftRadius: index === 0 ? "16px" : "0px",
                  borderTopRightRadius:
                    index === steps.length - 1 ? "16px" : "0px",
                  borderBottomRightRadius:
                    index === steps.length - 1 ? "16px" : "0px",
                }}
              >
                <div className="py-8 px-9 relative z-10">
                  <div className="flex flex-col">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-sm text-black text-lg font-bold mb-4">
                      {step.number}
                    </div>
                    <h3 className="text-lg md:text-xl font-bold font-body mb-3 text-gray-800">
                      {step.title}
                    </h3>
                    <p className="text-sm md:text-base text-gray-700 font-body leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Version with downward arrows */}
          <div className="flex flex-col md:hidden w-full mt-5 max-w-7xl">
            {steps.map((step, index) => (
             <div
                key={`mobile-${step.number}`}
                className={`
                  ${backgrounds[index % backgrounds.length]}
                  relative
                  w-full
                  ${index === 0 ? "rounded-t-2xl" : ""}
                  ${index === steps.length - 1 ? "rounded-b-2xl" : ""}
                `}
                style={{
                  clipPath:
                    index === 0
                      ? "polygon(0 0, 100% 0, 100% calc(100% - 25px), 50% 100%, 0 calc(100% - 25px))"
                      : index === steps.length - 1
                      ? "polygon(0 0, 50% 25px, 100% 0, 100% 100%, 0 100%)"
                      : "polygon(0 0, 50% 25px, 100% 0, 100% calc(100% - 25px), 50% 100%, 0 calc(100% - 25px))",
                  marginTop: index !== 0 ? "-25px" : "0",
                  zIndex: steps.length - index,
                  paddingTop: index !== 0 ? "25px" : "0",
                }}
              >
                <div className="py-5 px-6 relative z-10">
                  <div className="text-center flex flex-col items-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-sm text-black text-xl font-bold mb-2">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-bold font-body mb-1 text-gray-800">
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-700 font-body leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcedureSteps;